import type { Carer } from '@lmc/ts-common'
import { UserRole } from '@lmc/ts-common'
import { reportsConstants } from 'locale/team'

export enum RoleTabId {
  AllMembers = 'all-members',
  GroupAdmin = 'group-admin',
  GroupManager = 'group-manager',
  Admin = 'admin',
  Management = 'management',
  SeniorCarer = 'senior-carer',
  Carer = 'carer',
  ItSecurity = 'it-security',
  AuditView = 'audit-view'
}

// The single-site team list shows seven tabs. The group screen opts in to the
// two extra group-scoped tabs (Group Admin / Group Manager) via
// `includeGroupRoles` on `RoleTabs`.
const GROUP_ROLE_TAB_IDS: readonly RoleTabId[] = [
  RoleTabId.GroupAdmin,
  RoleTabId.GroupManager
]

export const ROLE_TAB_IDS: readonly RoleTabId[] = [
  RoleTabId.AllMembers,
  RoleTabId.Admin,
  RoleTabId.Management,
  RoleTabId.SeniorCarer,
  RoleTabId.Carer,
  RoleTabId.ItSecurity,
  RoleTabId.AuditView
]

// Ordered to match Figma 8363-41901: All members · Group Admin · Group Manager
// · Admin · Management · Senior Carer · Carer · IT & Security · Audit View.
export const GROUP_ROLE_TAB_IDS_ORDERED: readonly RoleTabId[] = [
  RoleTabId.AllMembers,
  RoleTabId.GroupAdmin,
  RoleTabId.GroupManager,
  RoleTabId.Admin,
  RoleTabId.Management,
  RoleTabId.SeniorCarer,
  RoleTabId.Carer,
  RoleTabId.ItSecurity,
  RoleTabId.AuditView
]

const ROLE_TAB_TO_USER_ROLE: Record<
  Exclude<RoleTabId, RoleTabId.AllMembers>,
  string
> = {
  [RoleTabId.GroupAdmin]: UserRole.groupAdmin,
  [RoleTabId.GroupManager]: UserRole.groupManager,
  [RoleTabId.Admin]: UserRole.careHomeAdmin,
  [RoleTabId.Management]: UserRole.management,
  [RoleTabId.SeniorCarer]: UserRole.seniorCarer,
  [RoleTabId.Carer]: UserRole.carer,
  [RoleTabId.ItSecurity]: UserRole.it,
  [RoleTabId.AuditView]: UserRole.auditView
}

// Returns the tab ids to render. Group roles are opt-in: the single-site list
// shows seven tabs, the group screen shows all nine.
export const getRoleTabIds = (
  includeGroupRoles = false
): readonly RoleTabId[] =>
  includeGroupRoles ? GROUP_ROLE_TAB_IDS_ORDERED : ROLE_TAB_IDS

// Count-caption key per tab (CM-4559). Each key pluralises on `count`, so the
// caller passes the number through i18next rather than building the sentence.
export const ROLE_TAB_COUNT_KEYS: Record<RoleTabId, string> = {
  [RoleTabId.AllMembers]: reportsConstants.TEAM_LIST_COUNT_ALL_MEMBERS,
  [RoleTabId.GroupAdmin]: reportsConstants.TEAM_LIST_COUNT_GROUP_ADMIN,
  [RoleTabId.GroupManager]: reportsConstants.TEAM_LIST_COUNT_GROUP_MANAGER,
  [RoleTabId.Admin]: reportsConstants.TEAM_LIST_COUNT_ADMIN,
  [RoleTabId.Management]: reportsConstants.TEAM_LIST_COUNT_MANAGEMENT,
  [RoleTabId.SeniorCarer]: reportsConstants.TEAM_LIST_COUNT_SENIOR_CARER,
  [RoleTabId.Carer]: reportsConstants.TEAM_LIST_COUNT_CARER,
  [RoleTabId.ItSecurity]: reportsConstants.TEAM_LIST_COUNT_IT_SECURITY,
  [RoleTabId.AuditView]: reportsConstants.TEAM_LIST_COUNT_AUDIT_VIEW
}

// Family role members never appear on the new Team list.
export const isListableCarer = (carer: Carer): boolean =>
  carer.role !== UserRole.family

export const carersForTab = (carers: Carer[], tab: RoleTabId): Carer[] => {
  if (tab === RoleTabId.AllMembers) {
    return carers
  }
  const role = ROLE_TAB_TO_USER_ROLE[tab]
  return carers.filter((c) => c.role === role)
}

export const isRoleTabId = (
  value: unknown,
  includeGroupRoles = false
): value is RoleTabId => {
  if (typeof value !== 'string') {
    return false
  }
  if (
    !includeGroupRoles &&
    (GROUP_ROLE_TAB_IDS as readonly string[]).includes(value)
  ) {
    return false
  }
  return (Object.values(RoleTabId) as readonly string[]).includes(value)
}
